import { Injectable } from '@angular/core';
import { AuthenticationStorageInterface, AuthResponse } from '@client-monorepo/common/user';
import { BeforeLoginRouteModel } from '../models/before-login-route.model';
import { SubscriptionSchemaModel } from '../models/subscription-schema.model';
import { ForgetPasswordSchemaModel } from '../models/forget-password-schema.model';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private subscriptionStorageKey = '__subscription_storage';
  private forgetPasswordStorageKey = '__forget_password_storage';
  private CALLBACK_URL = '__callback_url';
  private readonly USER_ID_STORAGE_KEY = '__dp_userId';

  getUserId(): string {
    const dps = localStorage.getItem('__dp_storage');
    if (dps) {
      const dpsJson = JSON.parse(dps);
      if (dpsJson?.auth?.userId) {
        return dpsJson.auth.userId;
      }
      const dpUserId = localStorage.getItem(this.USER_ID_STORAGE_KEY);
      if (dpUserId) {
        return JSON.parse(dpUserId);
      }
      return '';
    }
    return '';
  }

  setUserData(data: { userId: string; phoneNumber: string }) {
    const dps = localStorage.getItem('__dp_storage');
    if (dps) {
      const dpsJson = JSON.parse(dps);
      dpsJson.auth = {
        ...data,
      };
      localStorage.setItem('__dp_storage', JSON.stringify(dpsJson));
      localStorage.setItem(this.USER_ID_STORAGE_KEY, JSON.stringify(dpsJson?.auth?.userId));
    } else {
      const newDps = {
        auth: {
          ...data,
        },
      };
      localStorage.setItem(this.USER_ID_STORAGE_KEY, JSON.stringify(newDps?.auth?.userId));
      localStorage.setItem('__dp_storage', JSON.stringify(newDps));
    }
  }

  getUserData() {
    const dps = localStorage.getItem('__dp_storage');
    if (dps) {
      const dpsJson = JSON.parse(dps);
      return dpsJson.auth;
    }
    return '';
  }

  public getRefreshToken(): string | null {
    const auth: string | null = localStorage.getItem('__dp_storage');
    const authObject: AuthenticationStorageInterface | null = auth ? JSON.parse(auth) : null;
    return authObject?.auth.refresh || null;
  }

  public getToken(): string | null {
    const auth: string | null = localStorage.getItem('__dp_storage');
    const authObject: AuthenticationStorageInterface | null = auth ? JSON.parse(auth) : null;
    return authObject?.auth?.access || null;
  }

  public isLoggedIn(): boolean {
    return !!this.getToken();
  }

  public storeBeforeLoginRoute(route: BeforeLoginRouteModel): void {
    sessionStorage.setItem('__dp_before_login_route', JSON.stringify(route));
  }

  public getBeforeLoginRoute(): BeforeLoginRouteModel | null {
    return sessionStorage.getItem('__dp_before_login_route') ? JSON.parse(sessionStorage.getItem('__dp_before_login_route')!) : null;
  }

  public storeOnBoardingChecked(): void {
    localStorage.setItem('__dp_onboarding_checked', 'true');
  }

  public isOnboardingChecked(): boolean {
    return localStorage.getItem('__dp_onboarding_checked') === 'true';
  }

  setWalkThroughSate(rootElementId: string, step: string): void {
    const item = localStorage.getItem(rootElementId);
    if (item) {
      const itemJson = JSON.parse(item);
      itemJson.step = step;
      localStorage.setItem(rootElementId, JSON.stringify(itemJson));
    } else {
      const newItem = { step };
      localStorage.setItem(rootElementId, JSON.stringify(newItem));
    }
  }

  getWalkThroughSate(rootElementId: string): string | null {
    const item = localStorage.getItem(rootElementId);
    if (item) {
      return JSON.parse(item).step;
    } else {
      return null;
    }
  }

  public updateAuth(response: AuthResponse): void {
    const auth = localStorage.getItem('__dp_storage');
    const authObject = auth
      ? JSON.parse(auth)
      : {
          auth: {},
        };
    authObject.auth.access = response.accessToken;
    authObject.auth.refresh = response.refreshToken;
    authObject.auth.userId = response.userId || authObject.auth.userId;
    const dpUserId = localStorage.getItem(this.USER_ID_STORAGE_KEY);
    if (!dpUserId) {
      localStorage.setItem(this.USER_ID_STORAGE_KEY, JSON.stringify(authObject.auth?.userId));
    }
    localStorage.setItem('__dp_storage', JSON.stringify(authObject));
  }

  public setCardHistory(srcCardIndex: number | string, data: any): void {
    const storage = localStorage.getItem('__dp_storage');
    let storageObject = storage ? JSON.parse(storage) : null;
    storageObject = {
      ...storageObject,
      cardHistory: {
        ...storageObject.cardHistory,
        [srcCardIndex]: data,
      },
    };
    localStorage.setItem('__dp_storage', JSON.stringify(storageObject));
  }

  public getCardHistory() {
    const storage = localStorage.getItem('__dp_storage');
    const storageObject = storage ? JSON.parse(storage) : null;
    return storageObject?.cardHistory || null;
  }

  public removeCardHistory(cardIndex: PropertyKey): void {
    const storage = localStorage.getItem('__dp_storage');
    let storageObject = storage ? JSON.parse(storage) : null;
    const data = storageObject?.cardHistory;
    delete data[cardIndex];
    storageObject = { ...storageObject, cardHistory: { ...data } };
    localStorage.setItem('__dp_storage', JSON.stringify(storageObject));
  }

  public getSubscriptionStorage(): SubscriptionSchemaModel {
    const getSubscriptionStorage = localStorage.getItem(this.subscriptionStorageKey);
    if (getSubscriptionStorage) {
      return JSON.parse(getSubscriptionStorage);
    }
    return {};
  }

  public setSubscriptionStorage(subscriptionParams: SubscriptionSchemaModel): void {
    localStorage.setItem(this.subscriptionStorageKey, JSON.stringify({ ...this.getSubscriptionStorage(), ...subscriptionParams }));
  }

  public removeSubscriptionStorage(): void {
    const getSubscriptionStorage = localStorage.getItem(this.subscriptionStorageKey);
    if (getSubscriptionStorage) {
      localStorage.removeItem(this.subscriptionStorageKey);
    }
  }

  public getForgetPasswordStorage(): ForgetPasswordSchemaModel {
    const getStorage = localStorage.getItem(this.forgetPasswordStorageKey);
    if (getStorage) {
      return JSON.parse(getStorage);
    }
    return {};
  }

  public setForgetPasswordStorage(forgetPasswordParams: ForgetPasswordSchemaModel): void {
    localStorage.setItem(this.forgetPasswordStorageKey, JSON.stringify({ ...this.getForgetPasswordStorage(), ...forgetPasswordParams }));
  }

  public removeForgetPasswordStorage(): void {
    const getStorage = localStorage.getItem(this.forgetPasswordStorageKey);
    if (getStorage) {
      localStorage.removeItem(this.forgetPasswordStorageKey);
    }
  }

  public getRedirectUrlAfterLogin(): string | null {
    return sessionStorage.getItem('redirectUrlAfterLogin');
  }

  public removeRedirectUrlAfterLogin(): void {
    sessionStorage.removeItem('redirectUrlAfterLogin');
  }

  private getEscrowProperty<T>(key: string, defaultValue: T): T {
    const storageData = sessionStorage.getItem('__dp_storage_escrow');
    const storage = storageData ? JSON.parse(storageData) : { escrow: {} };
    return storage.escrow[key] ?? defaultValue;
  }

  private setEscrowProperty(key: string, value: any): void {
    const storageData = sessionStorage.getItem('__dp_storage_escrow');
    const storage = storageData ? JSON.parse(storageData) : { escrow: {} };
    storage.escrow = { ...storage.escrow, [key]: value };
    sessionStorage.setItem('__dp_storage_escrow', JSON.stringify(storage));
  }

  initEscrowProperties() {
    this.setEscrowUUID('');
    this.setEscrowCellNumber('');
    this.setEscrowTrustedLogin(false);
    this.setEscrowTrackingCode('');
  }

  setEscrowUUID(uuid: string | null): void {
    this.setEscrowProperty('uuid', uuid);
  }

  setEscrowIsMerchant(isMerchant: boolean): void {
    this.setEscrowProperty('isMerchant', isMerchant);
  }

  getEscrowIsMerchant(): boolean {
    return this.getEscrowProperty('isMerchant', false);
  }

  setEscrowSellerOnboarding(isOnboard: boolean): void {
    this.setEscrowProperty('isOnboard', isOnboard);
  }

  getEscrowSellerOnboarding(): boolean {
    return this.getEscrowProperty('isOnboard', false);
  }

  getEscrowUUID(): string {
    return this.getEscrowProperty('uuid', '');
  }

  setEscrowCellNumber(cellNumber: string | null): void {
    this.setEscrowProperty('cellNumber', cellNumber);
  }

  getEscrowCellNumber(): string {
    return this.getEscrowProperty('cellNumber', '');
  }

  setEscrowTrackingCode(trackingCode: string | null): void {
    this.setEscrowProperty('trackingCode', trackingCode);
  }

  getEscrowTrackingCode(): string {
    return this.getEscrowProperty('trackingCode', '');
  }

  setEscrowTrustedLogin(isTrusted: boolean): void {
    this.setEscrowProperty('isTrusted', isTrusted);
  }

  getEscrowTrustedLogin(): boolean {
    return this.getEscrowProperty('isTrusted', false);
  }

  setEscrowPluginActivationReturnUrl(returnUrl: string): void {
    this.setEscrowProperty('returnUrl', returnUrl);
  }

  getEscrowPluginActivationReturnUrl(): string {
    return this.getEscrowProperty('returnUrl', '');
  }

  setEscrowPluginActivationPostToken(postToken: string): void {
    this.setEscrowProperty('postToken', postToken);
  }

  getEscrowPluginActivationPostToken(): string {
    return this.getEscrowProperty('postToken', '');
  }

  setEscrowBottomSheetOnboarding(onboarding: boolean): void {
    this.setEscrowProperty('onboarding', onboarding);
  }

  getEscrowBottomSheetOnboarding(): string {
    return this.getEscrowProperty('onboarding', '');
  }

  setEscrowLastUserRole(userRole: string): void {
    this.setEscrowProperty('userRole', userRole);
  }

  getEscrowLastUserRole(): string {
    return this.getEscrowProperty('userRole', '');
  }
  setPassword(): void {
    localStorage.setItem('isSetPassword', 'true');
  }
  isSetPassword(): boolean {
    return localStorage.getItem('isSetPassword') === 'true';
  }
  removePasswordData() {
    localStorage.removeItem('isSetPassword');
  }

  getCallbackUrl() {
    return sessionStorage.getItem(this.CALLBACK_URL);
  }

  saveCallbackUrl(value: string): void {
    sessionStorage.setItem(this.CALLBACK_URL, value);
  }

  public setTicketData(ticket: string, data: any): void {
    const storage = localStorage.getItem('__dp_storage');
    let storageObject = storage ? JSON.parse(storage) : null;
    storageObject = {
      ...storageObject,
      tickets: {
        ...storageObject.tickets,
        [ticket]: data,
      },
    };
    localStorage.setItem('__dp_storage', JSON.stringify(storageObject));
  }

  public getTicketData() {
    const storage = localStorage.getItem('__dp_storage');
    const storageObject = storage ? JSON.parse(storage) : null;
    return storageObject?.tickets || null;
  }

  public removeTicketData(ticket: PropertyKey): void {
    const storage = localStorage.getItem('__dp_storage');
    let storageObject = storage ? JSON.parse(storage) : null;
    const data = storageObject?.tickets;
    delete data[ticket];
    storageObject = { ...storageObject, tickets: { ...data } };
    localStorage.setItem('__dp_storage', JSON.stringify(storageObject));
  }

  public addResolveUrl(url: string): void {
    localStorage.setItem('dp_target', url);
  }
  setHasBiometric(): void {
    localStorage.setItem('hasBiometric', 'true');
  }
  hasBiometric(): boolean {
    return localStorage.getItem('hasBiometric') === 'true';
  }
  removeHasBiometric() {
    localStorage.removeItem('hasBiometric');
  }

  getSearchHistory(): Array<string> {
    const history = localStorage.getItem('searchHistory');
    if (history) {
      return JSON.parse(history) as Array<string>;
    } else {
      return [];
    }
  }

  setSearchHistory(history: Array<string>): void {
    localStorage.setItem('searchHistory', JSON.stringify(history));
  }
  setTimeStamp(time: number): void {
    localStorage.setItem('timeStamp', JSON.stringify(time));
  }
  getTimeStamp(): string | null {
    const timeStampValue: string | null = localStorage.getItem('timeStamp');
    return timeStampValue || null;
  }
  removeTimeStamp(): void {
    localStorage.removeItem('timeStamp');
  }
}
